import { useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import SystemIdContext from "../SystemIdContext";
import {
  selectSystemConfig,
  selectIsNetworkConnected,
  selectLatestAlert,
  selectToastAlerts,
  selectSocketConnected,
  selectLanguage,
  selectServiceMode,
  selectOverviewUI,
  selectEdgeUnitStatusInfo,
  selectUI,
  selectFormattedEntries,
  selectExtraDiagnostics,
  selectRawSensorData,
  selectDataTimeStamp,
  selectLastSocketBatch,
  selectAlarmEntries,
  selectAlarmFormattedEntries,
  selectAllAlarms,
  selectAlarmsByHwId,
  selectDevicesConfigs,
  selectDeviceConfigEntries,
  selectSettingsConfig,
  selectReportsConfig,
  selectGraphicsConfigs,
  selectSamplesConfigs,
  selectListViewConfigs,
  selectSettingsUI,
  selectTrends,
  selectChartData,
  selectChartDataList,
  selectTrendLoading,
  selectDataLoadingStatus,
  selectSelectedDateTimeFilter,
  selectTimeFilterFrom,
  selectTimeFilterTo,
  selectDirectTimestampFrom,
  selectDirectTimestampTo,
  selectDevicesFilterList,
  selectDevicesLiveList,
  selectGraphics,
  selectAlarmsHistory,
  selectAlarmsLimits,
  selectReports,
  selectDevicesParameters,
  selectSamples,
  selectSolutionType,
  selectSettings,
  selectGeneralSettingsJson,
  selectSampleEntries,
  selectSelectedDevicesParameters,
  selectSelectedSystems,
  selectSelectedMapSystems,
  selectCustomers,
  selectCustomersList,
  makeSelectAlarmCounts,
  makeSelectDeviceAlarms,
  makeSelectSampleAlarms,
  makeSelectWarningAlarms,
  makeSelectAlarmsByCategory,
  makeSelectDevicesListForUI,
  makeSelectSamplesListForUI,
} from "../ducks/selectors";

// ─── System & Connection ─────────────────────────────────────────────────────

/** Get system config for the current systemId */
export const useSystemConfig = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSystemConfig(state, systemId));
};

/** Get network connection status */
export const useIsNetworkConnected = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectIsNetworkConnected(state, systemId));
};

/** Get latest toast alert */
export const useLatestAlert = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectLatestAlert(state, systemId));
};

/** Get toast alerts array */
export const useToastAlerts = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectToastAlerts(state, systemId));
};

/** Get socket connection status */
export const useSocketConnected = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSocketConnected(state, systemId));
};

/** Get solution type (cems, lora, etc.) */
export const useSolutionType = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSolutionType(state, systemId));
};

// ─── UI State ────────────────────────────────────────────────────────────────

/** Get the full UI state */
export const useUIState = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectUI(state, systemId));
};

/** Get language setting */
export const useLanguage = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectLanguage(state, systemId));
};

/** Get service mode state */
export const useServiceMode = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectServiceMode(state, systemId));
};

/** Get overview UI state */
export const useOverviewUI = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectOverviewUI(state, systemId));
};

/** Get edge unit status info */
export const useEdgeUnitStatusInfo = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectEdgeUnitStatusInfo(state, systemId));
};

/** Get settings UI toggles */
export const useSettingsUI = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSettingsUI(state, systemId));
};

// ─── Devices ─────────────────────────────────────────────────────────────────

/** Get the UI-ready devices list (derived from formattedEntries + ui + alarms) */
export const useDevicesList = () => {
  const systemId = useContext(SystemIdContext);
  const selectDevicesListForUI = useMemo(makeSelectDevicesListForUI, []);
  return useSelector((state) => selectDevicesListForUI(state, systemId));
};

/** Get formatted device entries (hwId → valueName → value) */
export const useFormattedEntries = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectFormattedEntries(state, systemId));
};

/** Get extra diagnostics (values without config) */
export const useExtraDiagnostics = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectExtraDiagnostics(state, systemId));
};

/** Get raw sensor data (LoRa cloud) */
export const useRawSensorData = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectRawSensorData(state, systemId));
};

/** Get device data timestamp */
export const useDataTimeStamp = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDataTimeStamp(state, systemId));
};

/** Get last raw socket data batch (for live trend subscription) */
export const useLastSocketBatch = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectLastSocketBatch(state, systemId));
};

// ─── Alarms ──────────────────────────────────────────────────────────────────

/** Get alarm entries (grouped by hwId) — legacy */
export const useAlarmEntries = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectAlarmEntries(state, systemId));
};

/** Get formatted alarm entries — legacy */
export const useAlarmFormattedEntries = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectAlarmFormattedEntries(state, systemId));
};

/** Get all alarms (flat array) */
export const useAllAlarms = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectAllAlarms(state, systemId));
};

/** Get alarms grouped by hwId */
export const useAlarmsByHwId = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectAlarmsByHwId(state, systemId));
};

/** Get active alarm counts by category — memoized */
export const useAlarmCounts = () => {
  const systemId = useContext(SystemIdContext);
  const selector = useMemo(makeSelectAlarmCounts, []);
  return useSelector((state) => selector(state, systemId));
};

/** Get active device alarms — memoized */
export const useDeviceAlarms = () => {
  const systemId = useContext(SystemIdContext);
  const selector = useMemo(makeSelectDeviceAlarms, []);
  return useSelector((state) => selector(state, systemId));
};

/** Get active sample alarms — memoized */
export const useSampleAlarms = () => {
  const systemId = useContext(SystemIdContext);
  const selector = useMemo(makeSelectSampleAlarms, []);
  return useSelector((state) => selector(state, systemId));
};

/** Get active warning alarms — memoized */
export const useWarningAlarms = () => {
  const systemId = useContext(SystemIdContext);
  const selector = useMemo(makeSelectWarningAlarms, []);
  return useSelector((state) => selector(state, systemId));
};

/** Get alarms filtered by category name — memoized */
export const useAlarmsByCategory = (category) => {
  const systemId = useContext(SystemIdContext);
  const selector = useMemo(makeSelectAlarmsByCategory, []);
  return useSelector((state) => selector(state, systemId, category));
};

// ─── Device Configs ──────────────────────────────────────────────────────────

/** Get formatted device configs (hwId → valueName → config) */
export const useDevicesConfigs = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDevicesConfigs(state, systemId));
};

/** Get raw device config entries */
export const useDeviceConfigEntries = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDeviceConfigEntries(state, systemId));
};

/** Get settings config (which settings views to show) */
export const useSettingsConfig = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSettingsConfig(state, systemId));
};

/** Get reports config */
export const useReportsConfig = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectReportsConfig(state, systemId));
};

/** Get graphics configs */
export const useGraphicsConfigs = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectGraphicsConfigs(state, systemId));
};

/** Get samples configs */
export const useSamplesConfigs = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSamplesConfigs(state, systemId));
};

/** Get list view configs */
export const useListViewConfigs = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectListViewConfigs(state, systemId));
};

// ─── Trends ──────────────────────────────────────────────────────────────────

/** Get full trends data */
export const useTrends = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectTrends(state, systemId));
};

/** Get chart data list (all 4 charts) — more granular than useTrends() */
export const useChartDataList = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectChartDataList(state, systemId));
};

/** Get chart data for a specific chart key (chart1-chart4) */
export const useChartData = (chart) => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectChartData(state, systemId, chart));
};

/** Get trend loading status */
export const useTrendLoading = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectTrendLoading(state, systemId));
};

/** Get InfluxDB data loading status */
export const useDataLoadingStatus = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDataLoadingStatus(state, systemId));
};

/** Get selected datetime filter (Live/Hour/Day/Week/Month/Year/Custom) */
export const useSelectedDateTimeFilter = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSelectedDateTimeFilter(state, systemId));
};

/** Get time filter from offset */
export const useTimeFilterFrom = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectTimeFilterFrom(state, systemId));
};

/** Get time filter to offset */
export const useTimeFilterTo = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectTimeFilterTo(state, systemId));
};

/** Get direct timestamp from (unix) */
export const useDirectTimestampFrom = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDirectTimestampFrom(state, systemId));
};

/** Get direct timestamp to (unix) */
export const useDirectTimestampTo = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDirectTimestampTo(state, systemId));
};

/** Get devices filter list (chart1-4 → hwId → valueName → config) */
export const useDevicesFilterList = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDevicesFilterList(state, systemId));
};

/** Get devices live list */
export const useDevicesLiveList = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDevicesLiveList(state, systemId));
};

// ─── Other Slices ────────────────────────────────────────────────────────────

/** Get graphics state (SVG strings, pending commands) */
export const useGraphics = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectGraphics(state, systemId));
};

/** Get alarms history */
export const useAlarmsHistory = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectAlarmsHistory(state, systemId));
};

/** Get alarm limits */
export const useAlarmsLimits = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectAlarmsLimits(state, systemId));
};

/** Get reports data */
export const useReports = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectReports(state, systemId));
};

/** Get device parameters */
export const useDevicesParameters = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectDevicesParameters(state, systemId));
};

/** Get samples data */
export const useSamples = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSamples(state, systemId));
};

/** Get settings data */
export const useSettingsData = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSettings(state, systemId));
};

/** Get GeneralSettings JSON data */
export const useGeneralSettingsJson = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectGeneralSettingsJson(state, systemId));
};

/** Get sample entries configs */
export const useSampleEntries = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSampleEntries(state, systemId));
};

/** Get selected devices parameters */
export const useSelectedDevicesParameters = () => {
  const systemId = useContext(SystemIdContext);
  return useSelector((state) => selectSelectedDevicesParameters(state, systemId));
};

/** Get samples list (derived from sample formattedEntries + ui + alarms) */
export const useSamplesList = () => {
  const systemId = useContext(SystemIdContext);
  const selectSamplesListForUI = useMemo(makeSelectSamplesListForUI, []);
  return useSelector((state) => selectSamplesListForUI(state, systemId));
};

// ─── Cloud-only ──────────────────────────────────────────────────────────────

/** Get full customers state (cloud only) */
export const useCustomers = () => useSelector(selectCustomers);

/** Get customers list (cloud only) */
export const useCustomersList = () => useSelector(selectCustomersList);

/** Get selected systems (cloud only) */
export const useSelectedSystems = () => useSelector(selectSelectedSystems);

/** Get selected map systems (cloud only) */
export const useSelectedMapSystems = () => useSelector(selectSelectedMapSystems);

// ─── Utility ─────────────────────────────────────────────────────────────────

/** Get systemId from context (convenience re-export) */
export const useSystemId = () => useContext(SystemIdContext);
